import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import React, { useEffect, useState, useRef } from "react";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { UploadMedia } from "../utils/uploadMedia";
import { showToast, showError } from "../helpers";
import { useLocation } from "react-router-dom";

const EditText = (props: {
    content: string;
    setContent: (arg0: string) => void;
    direction?: "ltr" | "rtl";
    textAlign?: "left" | "right" | "center" | "justify";
}) => {
    const [editorState, setEditorState] = useState(() => {
        try {
            if (props.content && props.content !== " " && props.content.trim() !== "") {
                const blocksFromHtml = htmlToDraft(props.content);
                const contentState = ContentState.createFromBlockArray(
                    blocksFromHtml.contentBlocks,
                    blocksFromHtml.entityMap
                );
                return EditorState.createWithContent(contentState);
            }
        } catch (error) {
            console.error("Error parsing HTML content:", error);
            // Fallback to empty editor if parsing fails
        }
        return EditorState.createEmpty();
    });

    const [isUploading, setIsUploading] = useState(false);
    const isInternalChange = useRef(false);
    const previousContent = useRef(props.content);
    const location = useLocation();

    // Sync external content changes to editor
    // Skip this effect if we're on manage-notifications path
    useEffect(() => {
        // Don't run this effect if we're on manage-notifications path
        if (location.pathname.includes("/manage-notifications")) {
            return;
        }

        if (!isInternalChange.current && props.content !== previousContent.current) {
            try {
                if (props.content && props.content !== " " && props.content.trim() !== "") {
                    const blocksFromHtml = htmlToDraft(props.content);
                    const contentState = ContentState.createFromBlockArray(
                        blocksFromHtml.contentBlocks,
                        blocksFromHtml.entityMap
                    );
                    const newEditorState = EditorState.createWithContent(contentState);
                    setEditorState(newEditorState);
                }
            } catch (error) {
                console.error("Error parsing HTML content in effect:", error);
            }
            previousContent.current = props.content;
        }
    }, [props.content, location.pathname]);

    const handleEditorStateChange = (newEditorState: typeof editorState) => {
        isInternalChange.current = true;
        setEditorState(newEditorState);
        // Reset flag after a short delay
        setTimeout(() => {
            isInternalChange.current = false;
        }, 0);
    };

    const uploadImageCallBack = async (file: File): Promise<{ data: { link: string } }> => {
        setIsUploading(true);
        try {
            const response = await UploadMedia(file);
            
            // Handle different response structures
            const imageUrl = response?.data?.image || response?.data?.url || response?.data?.link;
            
            if (!imageUrl) {
                throw new Error("No image URL received from server");
            }

            showToast("Image uploaded successfully");
            setIsUploading(false);
            
            return {
                data: { link: imageUrl }
            };
        } catch (error) {
            console.error("Error uploading image:", error);
            showError("Failed to upload image. Please try again.");
            setIsUploading(false);
            throw error;
        }
    };

    useEffect(() => {
        try {
            const text = draftToHtml(convertToRaw(editorState.getCurrentContent()));
            props.setContent(text);
        } catch (error) {
            console.error("Error converting editor content to HTML:", error);
        }
    }, [editorState]);

    const resolvedDirection = props.direction || "ltr";
    return (
        <div dir={resolvedDirection} key={resolvedDirection}>
            <Editor
                textAlignment={props.textAlign || (resolvedDirection === "rtl" ? "right" : "left")}
                editorClassName={resolvedDirection === "rtl" ? "force-rtl" : "force-ltr"}
                wrapperClassName={resolvedDirection === "rtl" ? "force-rtl" : "force-ltr"}
                toolbar={{
                    options: [
                        "inline",
                        "fontSize",
                        "list",
                        "textAlign",
                        "link",
                        "colorPicker",
                        "image",
                    ],
                    inline: {
                        options: ["bold", "italic", "underline"],
                    },
                    textAlign: {
                        options: ["left", "center", "right"],
                    },
                    list: {
                        options: ["unordered"],
                    },
                    image: {
                        uploadCallback: uploadImageCallBack,
                        previewImage: true,
                        alt: { present: true, mandatory: false },
                        inputAccept: "image/gif,image/jpeg,image/jpg,image/png,image/svg+xml",
                        defaultSize: {
                            height: "auto",
                            width: "auto",
                        },
                    },
                }}
                editorState={editorState}
                onEditorStateChange={handleEditorStateChange}
                editorStyle={{
                    height: "40vh",
                    border: "1px solid #E6E8F0",
                    padding: 10,
                    overflow: "auto",
                    direction: resolvedDirection,
                    unicodeBidi: resolvedDirection === "ltr" ? "bidi-override" : undefined,
                    textAlign: props.textAlign || (resolvedDirection === "rtl" ? "right" : "left"),
                }}
                wrapperStyle={{
                    position: "relative",
                }}
            />
            {isUploading && (
                <div style={{ 
                    position: "absolute", 
                    padding: "10px", 
                    background: "#f0f0f0", 
                    borderRadius: "4px",
                    marginTop: "10px"
                }}>
                    Uploading image...
                </div>
            )}
        </div>
    );
};
export default EditText;
